;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import React from "react";

// Types for condition logic
type ConditionOperator = "AND" | "OR";

// Base condition type
interface BaseCondition {
	operator?: ConditionOperator;
}

// Simple condition that checks a single value
interface SimpleCondition extends BaseCondition {
	value: any;
	expected?: any;
	customCheck?: (value: any) => boolean;
}

// Nested conditions for complex logic
interface NestedCondition extends BaseCondition {
	conditions: Condition[];
}

// Union type for all condition types
type Condition = SimpleCondition | NestedCondition;

// Props for the HOC
interface WithConditionProps {
	condition: Condition;
	fallback?: React.ReactNode;
	children: React.ReactNode;
}

// Props for the component that will be wrapped
interface ComponentProps {
	[key: string]: any;
}

// Main HOC function
export const withCondition = <P extends ComponentProps>(
	Component: React.ComponentType<P>,
	condition: Condition,
	fallback?: React.ReactNode,
) => {
	const WrappedComponent = (props: P) => {
		const shouldRender = evaluateCondition(condition, props);

		if (!shouldRender) {
			return fallback ? fallback : null;
		}

		return <Component {...props} />;
	};

	WrappedComponent.displayName = `withCondition(${Component.displayName || Component.name})`;

	return WrappedComponent;
};

// Hook version for functional components
export const useCondition = (condition: Condition, props: ComponentProps) => {
	return React.useMemo(
		() => evaluateCondition(condition, props),
		[condition, props],
	);
};

// Component version for inline usage
export const WithCondition: React.FC<WithConditionProps> = ({
	condition,
	fallback,
	children,
}) => {
	const shouldRender = React.useMemo(
		() => evaluateCondition(condition, {}),
		[condition],
	);

	if (!shouldRender) {
		return fallback ? fallback : null;
	}

	return <>{children}</>;
};

// Core function to evaluate conditions
const evaluateCondition = (
	condition: Condition,
	props: ComponentProps,
): boolean => {
	// Handle nested conditions
	if ("conditions" in condition) {
		const nestedConditions = condition.conditions;
		const operator = condition.operator || "AND";

		if (nestedConditions.length === 0) return true;

		const results = nestedConditions.map((nestedCondition) =>
			evaluateCondition(nestedCondition, props),
		);

		return operator === "AND"
			? results.every((result) => result)
			: results.some((result) => result);
	}

	// Handle simple conditions
	const { value, expected, customCheck, operator } = condition;

	// If custom check is provided, use it
	if (customCheck) {
		return customCheck(value);
	}

	// Default comparison
	const result = value === expected;

	// If no operator specified, return the result directly
	if (!operator) {
		return result;
	}

	return result;
};

// Utility functions for common conditions
export const createCondition = {
	// Combine conditions with AND
	and: (...conditions: Condition[]): NestedCondition => ({
		conditions,
		operator: "AND",
	}),

	// String contains check
	contains: (str: string, substring: string): SimpleCondition => ({
		customCheck: (s) => typeof s === "string" && s.includes(substring),
		value: str,
	}),

	// Custom function check
	custom: (value: any, check: (value: any) => boolean): SimpleCondition => ({
		customCheck: check,
		value,
	}),
	// Simple equality check
	equals: (value: any, expected: any): SimpleCondition => ({
		expected,
		value,
	}),

	// Falsy check
	falsy: (value: any): SimpleCondition => ({
		customCheck: (val) => !val,
		value,
	}),

	// Number comparison
	greaterThan: (value: number, threshold: number): SimpleCondition => ({
		customCheck: (val) => typeof val === "number" && val > threshold,
		value,
	}),

	// Array includes check
	includes: (array: any[], item: any): SimpleCondition => ({
		customCheck: (arr) => Array.isArray(arr) && arr.includes(item),
		value: array,
	}),

	lessThan: (value: number, threshold: number): SimpleCondition => ({
		customCheck: (val) => typeof val === "number" && val < threshold,
		value,
	}),

	// Combine conditions with OR
	or: (...conditions: Condition[]): NestedCondition => ({
		conditions,
		operator: "OR",
	}),

	// Truthy check
	truthy: (value: any): SimpleCondition => ({
		customCheck: (val) => !!val,
		value,
	}),
};

// Export types for external use
export type {
	ComponentProps,
	Condition,
	ConditionOperator,
	NestedCondition,
	SimpleCondition,
	WithConditionProps,
};
